import type { Metadata, MetadataRoute, Viewport } from "next";
import { coreServices, type CoreService } from "@/shared/config/coreServices";
import { customerSupportEmail, customerSupportPhone } from "@/shared/config/customerSupport";

/**
 * Single source of truth for editable SEO content and crawler settings.
 * Keep page content, image descriptions, and optional FAQs here when they are
 * also used by structured data so the visible page and crawler data stay aligned.
 * App Router metadata files should only expose the values and builders defined here.
 */

export const seoLocality = {
  primaryName: "Pondicherry",
  alternateName: "Puducherry",
  displayName: "Puducherry",
} as const;

const fallbackSiteUrl = "https://www.carevinco.com";

function resolveSiteUrl(value?: string) {
  try {
    const url = new URL(value?.trim() || fallbackSiteUrl);

    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return fallbackSiteUrl;
    }

    return url.origin;
  } catch {
    return fallbackSiteUrl;
  }
}

export const seoConfig = {
  defaultDescription:
    `Book verified home nurses and compassionate senior care services in ${seoLocality.displayName} with Carevinco.`,
  defaultTitle: "Carevinco | Trusted Home Nursing and Senior Care",
  language: "en",
  locale: "en_IN",
  siteName: "Carevinco",
  siteUrl: resolveSiteUrl(process.env.NEXT_PUBLIC_SITE_URL),
  socialImage: "/assets/corrected/landing/hero-care.webp",
  socialImageAlt: "A Carevinco home nurse supporting an older couple",
} as const;

const defaultSocialImage = {
  url: seoConfig.socialImage,
  alt: seoConfig.socialImageAlt,
  width: 800,
  height: 463,
} as const;

const nurseSocialImage = {
  url: "/assets/corrected/nurse/hero.webp",
  alt: "Home nurse caring for an elderly couple",
  width: 800,
  height: 463,
} as const;

const providerSocialImage = {
  url: "/assets/corrected/provider/hero-provider-optimized.webp",
  alt: "Home nurse caring for an elderly couple",
  width: 800,
  height: 463,
} as const;

const googleSiteVerification = process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION?.trim();
const bingSiteVerification = process.env.NEXT_PUBLIC_BING_SITE_VERIFICATION?.trim();

function absoluteUrl(path: string) {
  return new URL(path, `${seoConfig.siteUrl}/`).toString();
}

function createPublicMetadata({
  canonical,
  description,
  image = defaultSocialImage,
  noIndex = false,
  title,
}: {
  canonical: string;
  description: string;
  image?: typeof defaultSocialImage | typeof nurseSocialImage | typeof providerSocialImage;
  noIndex?: boolean;
  title: string;
}): Metadata {
  const socialTitle = `${title} | ${seoConfig.siteName}`;

  return {
    title,
    description,
    alternates: { canonical },
    ...(noIndex
      ? {
          robots: {
            index: false,
            follow: true,
            googleBot: { index: false, follow: true },
          },
        }
      : {}),
    openGraph: {
      type: "website",
      siteName: seoConfig.siteName,
      locale: seoConfig.locale,
      url: canonical,
      title: socialTitle,
      description,
      images: [image],
    },
    twitter: {
      card: "summary_large_image",
      title: socialTitle,
      description,
      images: [image.url],
    },
  };
}

export const seoKeywords = [
  "home nurse",
  "provider",
  "elderly care",
  "medical home help",
  "verified nurse",
  "verified nurses in Pondicherry",
  "verified nurses in Puducherry",
  "home healthcare",
  "home nurse Pondicherry",
  "home nurse Puducherry",
  "nurse after JIPMER discharge",
  "home nursing Puducherry",
  "home nursing Pondicherry",
  "home nurse for elderly Pondicherry",
  "home nurse for elderly Puducherry",
];

export const rootMetadata: Metadata = {
  metadataBase: new URL(seoConfig.siteUrl),
  applicationName: seoConfig.siteName,
  title: { default: seoConfig.defaultTitle, template: `%s | ${seoConfig.siteName}` },
  description: seoConfig.defaultDescription,
  authors: [{ name: seoConfig.siteName, url: "/" }],
  creator: seoConfig.siteName,
  publisher: seoConfig.siteName,
  category: "Home healthcare",
  keywords: seoKeywords,
  formatDetection: {
    email: false,
    address: false,
    telephone: false,
  },
  icons: {
    icon: [{ url: "/favicon.svg", sizes: "any", type: "image/svg+xml" }],
  },
  manifest: "/manifest.webmanifest",
  verification: {
    ...(googleSiteVerification ? { google: googleSiteVerification } : {}),
    ...(bingSiteVerification ? { other: { "msvalidate.01": bingSiteVerification } } : {}),
  },
  openGraph: {
    type: "website",
    siteName: seoConfig.siteName,
    locale: seoConfig.locale,
    title: seoConfig.defaultTitle,
    description: seoConfig.defaultDescription,
    images: [defaultSocialImage],
  },
  twitter: {
    card: "summary_large_image",
    title: seoConfig.defaultTitle,
    description: seoConfig.defaultDescription,
    images: [seoConfig.socialImage],
  },
};

export const rootViewport: Viewport = {
  width: "device-width",
  initialScale: 1,
  viewportFit: "cover",
  colorScheme: "light",
  themeColor: "#0754c5",
};

export const organizationStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${seoConfig.siteUrl}/#organization`,
  name: seoConfig.siteName,
  url: seoConfig.siteUrl,
  logo: absoluteUrl("/assets/brand/logo-mark-7cc959d9-5283-46c3-92ec-5f8be3d1ffea.svg"),
  email: customerSupportEmail,
  telephone: customerSupportPhone,
} as const;

const homeTitle = "Trusted Home Nursing and Senior Care";
const homeDescription =
  `Find verified home nurses and personalized senior care in ${seoLocality.displayName} through Carevinco. Book home nursing, post-surgery support, and elderly care.`;

export type SeoFaqItem = {
  answer: string;
  question: string;
};

export type SeoPageImage = {
  alt: string;
  height: number;
  url: string;
  width: number;
};

export type SeoPageContent = {
  faqs?: readonly SeoFaqItem[];
  images: Readonly<Record<string, SeoPageImage>>;
  pageContent: {
    description: string;
    heroDescription: string;
    heroTitle: {
      emphasis: string;
      lead: string;
    };
    title: string;
  };
};

export const homePageSeoContent = {
  pageContent: {
    title: homeTitle,
    description: homeDescription,
    heroTitle: {
      lead: "Your loved one is home. Your nurse is there. You're ",
      emphasis: "informed",
    },
    heroDescription:
      "Verified home nurses for your loved ones. Matched within hours. Real-time updates after every visit so you always know they're safe",
  },
  images: {
    hero: {
      url: "/assets/corrected/landing/hero-care-optimized.webp",
      alt: "A Carevinco home nurse supporting an older couple",
      width: 800,
      height: 463,
    },
    app: {
      url: "/assets/corrected/landing/app-mockup.webp",
      alt: "Carevinco mobile app showing home-care visit updates",
      width: 626,
      height: 661,
    },
  },
  faqs: [
    {
      question: "What is Carevinco?",
      answer:
        "Carevinco is a care management platform that helps families connect with verified caregivers, nurses, doctors, and wellness professionals while staying informed throughout the care journey.",
    },
    {
      question: "How do I request a care service?",
      answer:
        "Simply choose a service, tell us what you need, and our care team matches you with a verified nurse—usually within hours.",
    },
    {
      question: "What services does Carevinco provide?",
      answer:
        "We offer home nursing, elder care, post-hospital care, palliative care, and personalized wellness support delivered at home.",
    },
    {
      question: "Are all care providers verified?",
      answer:
        "Yes, every provider is identity-verified, background-checked, and vetted for qualifications before joining Carevinco.",
    },
    {
      question: "Can I choose my preferred nurse?",
      answer:
        "Absolutely. You can choose a nurse based on your preferred gender and language, so you can select someone who best fits your family's needs.",
    },
    {
      question: "How will I know what happens during a visit?",
      answer:
        "You'll receive real-time visit updates and care reports directly on the app after every visit.",
    },
    {
      question: "Can I book care services for my parents while living abroad?",
      answer:
        "Yes, families abroad can book, manage, and track care visits remotely with complete transparency.",
    },
    {
      question: "What happens if I am not satisfied with a provider?",
      answer:
        "Our care coordination team will help you switch providers or resolve any concerns promptly.",
    },
    {
      question: "Are the care plans personalized?",
      answer:
        "Every care plan is tailored to the individual's health needs, recovery goals, and daily routine.",
    },
    {
      question: "Is emergency support available?",
      answer:
        "Carevinco can help with care coordination and guidance, but it is not an emergency service. For urgent medical help, contact your local emergency services immediately.",
    },
    {
      question: "How do payments work?",
      answer:
        "Pricing is transparent and shown upfront—you only pay for the care you actually receive.",
    },
    {
      question: "Can I track appointments and schedules?",
      answer:
        "Yes, all appointments and schedules can be tracked in real time through the Carevinco app.",
    },
    {
      question: "Will I receive regular updates about my loved one?",
      answer:
        "Yes, you'll get regular updates and visit summaries so you always know how your loved one is doing.",
    },
    {
      question: "Is my personal information secure?",
      answer:
        "Your data is encrypted and handled with strict privacy and security standards at every step.",
    },
  ],
} as const satisfies SeoPageContent;

function createStructuredImage(image: SeoPageImage) {
  const url = absoluteUrl(image.url);

  return {
    "@type": "ImageObject",
    url,
    contentUrl: url,
    caption: image.alt,
    width: image.width,
    height: image.height,
  } as const;
}

const homePageUrl = absoluteUrl("/");
const homePageHeadline =
  `${homePageSeoContent.pageContent.heroTitle.lead}${homePageSeoContent.pageContent.heroTitle.emphasis}`;

// Page-scoped Schema.org data for machine readers. Search engines independently
// decide whether and how structured FAQ content appears in search results.
export const homePageStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${homePageUrl}#faq-page`,
  url: homePageUrl,
  name: homePageSeoContent.pageContent.title,
  headline: homePageHeadline,
  description: homePageSeoContent.pageContent.description,
  inLanguage: seoConfig.language,
  publisher: { "@id": `${seoConfig.siteUrl}/#organization` },
  primaryImageOfPage: createStructuredImage(homePageSeoContent.images.hero),
  image: [
    createStructuredImage(homePageSeoContent.images.hero),
    createStructuredImage(homePageSeoContent.images.app),
  ],
  mainEntity: homePageSeoContent.faqs.map(({ answer, question }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: {
      "@type": "Answer",
      text: answer,
    },
  })),
} as const;

const activeServicesTitle = coreServices.length === 1 ? coreServices[0].title : "Care Services";
const activeServicesDescription = coreServices.length
  ? `Explore ${coreServices.map((service) => service.title).join(", ")} from Carevinco, including ${coreServices
      .flatMap((service) => service.offerings)
      .slice(0, 4)
      .join(", ")} in ${seoLocality.displayName}.`
  : `Explore the currently available Carevinco home care services in ${seoLocality.displayName}.`;

export const nurseServiceSeoContent = {
  title: "Nurse Services",
  description: `Qualified nurses, personalized care, and real-time updates for families in ${seoLocality.displayName}.`,
} as const;

export const publicPageMetadata = {
  home: createPublicMetadata({
    title: homePageSeoContent.pageContent.title,
    description: homePageSeoContent.pageContent.description,
    canonical: "/",
  }),
  landingPage: createPublicMetadata({
    title: homePageSeoContent.pageContent.title,
    description: homePageSeoContent.pageContent.description,
    canonical: "/",
    noIndex: true,
  }),
  about: createPublicMetadata({
    title: "About Carevinco",
    description: "Learn how Carevinco provides compassionate, personalized in-home care for seniors and families.",
    canonical: "/about",
  }),
  benefits: createPublicMetadata({
    title: "Why Carevinco",
    description: "Explore why families choose Carevinco for safe, reliable, compassionate senior care at home.",
    canonical: "/",
    noIndex: true,
  }),
  contactUs: createPublicMetadata({
    title: "Contact Us",
    description: `Contact Carevinco for home nursing questions, in-home support, and service inquiries in ${seoLocality.displayName}.`,
    canonical: "/contact-us",
  }),
  forProviders: createPublicMetadata({
    title: "For Providers",
    description: "Explore the Carevinco provider journey, recruitment flow, professional support, and onboarding benefits.",
    canonical: "/for-providers",
    image: providerSocialImage,
  }),
  services: createPublicMetadata({
    title: activeServicesTitle,
    description: activeServicesDescription,
    canonical: "/services",
    image: nurseSocialImage,
  }),
  termsAndConditions: createPublicMetadata({
    title: "Terms & Conditions",
    description: "Read the terms that govern access to Carevinco's home-care platform, bookings, payments, and services.",
    canonical: "/terms-and-conditions",
  }),
  privacyPolicy: createPublicMetadata({
    title: "Privacy Policy",
    description: "Learn how Carevinco collects, uses, protects, and shares personal information across its care services.",
    canonical: "/privacy-policy",
  }),
} satisfies Record<string, Metadata>;

const servicePageMetadata: Record<string, Metadata> = {
  "nurse-services": createPublicMetadata({
    title: nurseServiceSeoContent.title,
    description: nurseServiceSeoContent.description,
    canonical: "/services/nurse-services",
    image: nurseSocialImage,
  }),
};

export function createServiceMetadata(service?: Pick<CoreService, "slug">): Metadata {
  if (!service) {
    return { title: "Service not found" };
  }

  const metadata = servicePageMetadata[service.slug];

  if (!metadata) {
    throw new Error(`Missing SEO metadata for service "${service.slug}" in src/shared/config/seo.ts.`);
  }

  return metadata;
}

export function createNurseServiceStructuredData(
  service: Pick<CoreService, "slug" | "title">,
) {
  if (service.slug !== "nurse-services") {
    throw new Error("Nurse service structured data can only be rendered for the active nurse-services page.");
  }

  const homeUrl = absoluteUrl("/");
  const servicesUrl = absoluteUrl("/services/");
  const serviceUrl = absoluteUrl("/services/nurse-services/");

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${serviceUrl}#service`,
        name: service.title,
        serviceType: service.title,
        description: nurseServiceSeoContent.description,
        url: serviceUrl,
        provider: { "@id": `${seoConfig.siteUrl}/#organization` },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${serviceUrl}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "Services", item: servicesUrl },
          { "@type": "ListItem", position: 3, name: service.title, item: serviceUrl },
        ],
      },
    ],
  } as const;
}

export const privateRouteMetadata: Metadata = {
  robots: {
    index: false,
    follow: false,
    googleBot: {
      index: false,
      follow: false,
      noarchive: true,
      noimageindex: true,
      nosnippet: true,
    },
  },
};

export const robotsMetadata: MetadataRoute.Robots = {
  rules: {
    userAgent: "*",
    allow: "/",
    disallow: ["/auth/", "/user/", "/provider/", "/admin/", "/super-admin/"],
  },
  sitemap: `${seoConfig.siteUrl}/sitemap.xml`,
  host: seoConfig.siteUrl,
};

const publicSitemapEntries = [
  { path: "/", changeFrequency: "weekly" as const, priority: 1 },
  { path: "/about/", changeFrequency: "monthly" as const, priority: 0.7 },
  { path: "/services/", changeFrequency: "monthly" as const, priority: 0.7 },
  { path: "/for-providers/", changeFrequency: "monthly" as const, priority: 0.7 },
  { path: "/contact-us/", changeFrequency: "monthly" as const, priority: 0.7 },
  { path: "/terms-and-conditions/", changeFrequency: "monthly" as const, priority: 0.4 },
  { path: "/privacy-policy/", changeFrequency: "monthly" as const, priority: 0.4 },
];

export function createSitemapMetadata(serviceSlugs: readonly string[]): MetadataRoute.Sitemap {
  const lastModified = new Date();

  return [
    ...publicSitemapEntries.map(({ path, changeFrequency, priority }) => ({
      url: `${seoConfig.siteUrl}${path}`,
      lastModified,
      changeFrequency,
      priority,
    })),
    ...serviceSlugs.map((slug) => ({
      url: `${seoConfig.siteUrl}/services/${slug}/`,
      lastModified,
      changeFrequency: "monthly" as const,
      priority: 0.8,
    })),
  ];
}
